// Heuristic table / figure region detection.
//
// pdf.js getTextContent flattens tables into stream-order text, so table
// cells land inline with prose. A table cell like "271" then masquerades as
// a declared sample size in the abstract/results consistency check, and
// figure-legend numbers add noise. We can't recover grid geometry from
// text alone, but we CAN bound the region that *follows a caption*
// ("Table 1 …", "Figure 2 …") and is dominated by short/numeric lines, then
// let numeric integrity treat those offsets as non-prose.
//
// Conservative by design: a region ends at the first real prose paragraph,
// the next caption, a blank gap, or a hard cap — so legitimate prose
// findings are never suppressed.

export interface TabularRegion {
  kind: 'table' | 'figure';
  /** Caption label, e.g. "Table 1" / "Figure 2". */
  label: string;
  /** Char offsets into rawText. */
  start: number;
  end: number;
}

const CAPTION_RE =
  /^(?:\d+\s*[.)]\s*)?(supplementary\s+|appendix\s+|online\s+)?(table|fig(?:ure)?\.?)\s*([0-9ivx]+)\b/i;

const MAX_REGION_CHARS = 1600;
const MAX_REGION_LINES = 30;

/** A line that is clearly running prose (ends a tabular region). */
function isProseLine(line: string): boolean {
  const t = line.trim();
  if (t.length < 60) return false;
  const letters = (t.match(/[A-Za-z]/g) ?? []).length;
  const alphaRatio = letters / t.length;
  // Long, mostly-alphabetic, sentence-like.
  return t.length >= 120 && alphaRatio >= 0.75 && /[.;]/.test(t);
}

export function detectTabularRegions(rawText: string): TabularRegion[] {
  const regions: TabularRegion[] = [];
  const lines = rawText.split('\n');
  let cursor = 0;
  const lineStart: number[] = [];
  for (const ln of lines) {
    lineStart.push(cursor);
    cursor += ln.length + 1;
  }

  for (let i = 0; i < lines.length; i++) {
    const cm = CAPTION_RE.exec(lines[i].trim());
    if (!cm) continue;
    const kind: TabularRegion['kind'] = /^fig/i.test(cm[2]) ? 'figure' : 'table';
    const label = `${/^fig/i.test(cm[2]) ? 'Figure' : 'Table'} ${cm[3]}`;
    const start = lineStart[i];

    // Extend through following lines while they are short/numeric, stopping
    // at prose, the next caption, a blank gap, or the hard cap.
    let end = start + lines[i].length;
    let blanks = 0;
    for (let j = i + 1; j < lines.length && j < i + 1 + MAX_REGION_LINES; j++) {
      const raw = lines[j];
      const t = raw.trim();
      if (t.length === 0) {
        if (++blanks >= 2) break;
        end = lineStart[j] + raw.length;
        continue;
      }
      blanks = 0;
      if (CAPTION_RE.test(t)) break; // next caption
      if (isProseLine(raw)) break; // back to running text
      end = lineStart[j] + raw.length;
      if (end - start > MAX_REGION_CHARS) break;
    }
    regions.push({ kind, label, start, end });
  }
  return mergeOverlaps(regions);
}

function mergeOverlaps(rs: TabularRegion[]): TabularRegion[] {
  if (rs.length <= 1) return rs;
  const sorted = [...rs].sort((a, b) => a.start - b.start);
  const out: TabularRegion[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    if (cur.start <= prev.end) {
      prev.end = Math.max(prev.end, cur.end);
      if (prev.label !== cur.label) prev.label = `${prev.label}, ${cur.label}`;
    } else {
      out.push(cur);
    }
  }
  return out;
}

export function offsetInTabularRegion(
  offset: number,
  regions: TabularRegion[] | undefined,
): boolean {
  if (!regions) return false;
  for (const r of regions) {
    if (offset >= r.start && offset < r.end) return true;
  }
  return false;
}
